import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useSmsTemplates } from "@/hooks/useSmsTemplates";
import { useAuth } from "@/context/AuthContext";
import { validateSmsMessage, previewSmsMessage, getAvailableVariables } from "@/utils/smsUtils";

interface SmsTemplateStepProps {
  onComplete: () => void;
  onPrevious: () => void;
}

const DEFAULT_TEMPLATE = "Olá {customer_name}! Você ganhou R$ {coupon_value} de cashback! Use o código {coupon_code} em compras acima de R$ {minimum_purchase_amount}. Aproveite!";

export const SmsTemplateStep = ({ onComplete, onPrevious }: SmsTemplateStepProps) => {
  const [templateName, setTemplateName] = useState("Template Padrão");
  const [templateMessage, setTemplateMessage] = useState(DEFAULT_TEMPLATE);
  const [isLoading, setIsLoading] = useState(false);
  
  const { toast } = useToast();
  const { user } = useAuth();
  const { createTemplate } = useSmsTemplates();

  const availableVariables = getAvailableVariables();
  const validation = validateSmsMessage(templateMessage);
  const previewMessage = previewSmsMessage(templateMessage);

  const insertVariable = (variable: string) => {
    setTemplateMessage(prev => prev + `{${variable}}`);
  };

  const handleSave = async () => {
    if (!user || !validation.isValid) return;

    setIsLoading(true);
    try {
      await createTemplate.mutateAsync({
        name: templateName,
        message: templateMessage,
        variables: availableVariables,
        is_default: true,
      });

      toast({
        title: "Template criado!",
        description: "Seu template de SMS padrão foi configurado com sucesso.",
      });

      onComplete();
    } catch (error) {
      console.error('Erro ao criar template:', error);
      toast({
        title: "Erro",
        description: "Não foi possível criar o template SMS",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="text-center space-y-2">
        <h2 className="text-xl font-semibold">Configurar Template SMS</h2>
        <p className="text-sm text-muted-foreground">
          Configure um template padrão para as notificações SMS de cashback
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="template-name" className="text-base">Nome do Template</Label>
          <input
            id="template-name"
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="Ex: Template Padrão"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-end gap-2">
            <Label htmlFor="template-message" className="text-base">Mensagem</Label>
            <span className="text-xs text-muted-foreground">(sugestão)</span>
          </div>
          <Textarea
            id="template-message"
            value={templateMessage}
            onChange={(e) => setTemplateMessage(e.target.value)}
            placeholder="Digite sua mensagem aqui..."
            className="min-h-[80px] resize-none"
          />
          <div className="flex justify-between items-center text-xs">
            <span className={`${templateMessage.length > 160 ? 'text-destructive' : 'text-muted-foreground'}`}>
              {templateMessage.length}/160 caracteres
            </span>
            {!validation.isValid && (
              <span className="text-destructive">{validation.error}</span>
            )}
          </div>
        </div>

        {/* Preview sempre visível - otimizado */}
        <div className="bg-muted/30 border border-muted rounded-md p-2 space-y-1">
          <p className="text-xs text-muted-foreground font-medium">Preview:</p>
          <p className="text-sm">{previewMessage}</p>
        </div>

        {/* Variáveis compactas */}
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium">Variáveis</Label>
            <span className="text-xs text-muted-foreground">• clique para inserir</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {availableVariables.map((variable) => (
              <Badge
                key={variable}
                variant="outline"
                className="cursor-pointer hover:bg-accent text-xs h-6 px-2"
                onClick={() => insertVariable(variable)}
              >
                {`{${variable}}`}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-between gap-3 pt-4">
        <Button
          variant="outline"
          onClick={onPrevious}
          disabled={isLoading}
          className="flex-1"
        >
          Voltar
        </Button>
        <Button
          onClick={handleSave}
          disabled={!validation.isValid || isLoading || !templateName.trim()}
          className="flex-1"
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isLoading ? "Salvando..." : "Continuar"}
        </Button>
      </div>
    </div>
  );
};